/**
 * Kalender-Abos (iCal) in den persönlichen Einstellungen.
 *
 * Seit 2026-09-30 (Regel „Dokument-Navigation", Abhakliste 1.47): Löschen fragt über
 * das Portal-Dialogfenster nach (kein Fenster des Browsers), Fehler erscheinen als
 * Meldung, der QR-Code ist eine reine Anzeige (Escape und Klick daneben schließen).
 * Anlegen eines Abos ist eine Einzel-Aktion (E4) mit sichtbarer Rückmeldung.
 */
import { useCallback, useEffect, useState } from 'react';
import { Calendar, Plus, Trash2, Copy, Check, Loader2, QrCode, RefreshCw } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { GuardedModal, useDialogs, useDirtySnapshot } from '@onvice/portal-ui';

interface TokenRow {
  id: string;
  token: string;
  description: string | null;
  scopes: string[];
  created_at: string;
  last_accessed_at: string | null;
}

const ALL_SCOPES: Array<{ key: string; label: string }> = [
  { key: 'personal', label: 'Eigene Termine' },
  { key: 'tasks',    label: 'Aufgaben' },
  { key: 'absences', label: 'Abwesenheiten' },
  { key: 'todos',    label: 'Todos' },
  // 2026-07-03: fuhrpark-Scope nachgezogen — Backend hatte die Quelle schon,
  // im Subscribe-Card fehlte sie. HU/AU-, Reifen- und Fuehrerschein-Termine
  // landen dadurch im iCal-Feed.
  { key: 'fuhrpark', label: 'Fuhrpark (HU/AU, Reifen, Fuehrerschein)' },
  // 2026-07-05: ressourcen-Scope. Wartungen aus ressourcen.maintenance_schedules.
  { key: 'ressourcen', label: 'Ressourcen (Wartungen, DGUV, Kalibrierung)' },
  // 2026-07-03: holidays-Scope. Region kommt vom Backend (TENANT_REGION, default NW).
  { key: 'holidays', label: 'Feiertage' },
];

export default function KalenderSubscribeCard() {
  const dlg = useDialogs();
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newDesc, setNewDesc] = useState('');
  const [newScopes, setNewScopes] = useState<string[]>(ALL_SCOPES.map((s) => s.key));
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // 2026-07-03: QR-Code-Popup. openQr haelt die Token-Row des aktuell
  // angezeigten Abos, oder null wenn geschlossen.
  const [openQr, setOpenQr] = useState<TokenRow | null>(null);
  const [newSaved, setNewSaved] = useState<string | null>(null);
  // Das Anlege-Formular meldet Eingaben beim Verlassen-Schutz — Ausnahme E4 (Einzel-Aktion),
  // aber angefangene Eingaben gehen nicht still verloren.
  const newDirty = useDirtySnapshot(showNew, { newDesc, newScopes });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get<{ success: boolean; tokens: TokenRow[] }>('/kalender/subscribe-tokens');
      setTokens(r.tokens ?? []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const create = async (): Promise<void> => {
    setCreating(true);
    setError('');
    try {
      await api.post('/kalender/subscribe-tokens', {
        description: newDesc.trim() || undefined,
        scopes: newScopes,
      });
      const label = newDesc.trim() || 'Kalender-Abo';
      setNewDesc('');
      setNewScopes(ALL_SCOPES.map((s) => s.key));
      setShowNew(false);
      setNewSaved(`Abo „${label}“ angelegt.`);
      await load();
    } catch (err) {
      await dlg.error('Abo wurde nicht angelegt', err);
    } finally {
      setCreating(false);
    }
  };

  const cancelNew = async (): Promise<void> => {
    if (newDirty) {
      const ok = await dlg.confirm({ title: 'Eingaben verwerfen?', message: 'Das neue Abo wurde noch nicht angelegt.', confirmLabel: 'Verwerfen', cancelLabel: 'Weiter bearbeiten', danger: true });
      if (!ok) return;
    }
    setShowNew(false);
    setNewDesc('');
    setNewScopes(ALL_SCOPES.map((s) => s.key));
  };

  const remove = async (t: TokenRow): Promise<void> => {
    const label = t.description || `Token ${t.token.slice(0, 8)}…`;
    const ok = await dlg.confirm({
      title: `Kalender-Abo „${label}“ löschen?`,
      message: 'Alle Geräte und Apps, die dieses Abo nutzen, verlieren den Zugriff.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/kalender/subscribe-tokens/${t.id}`);
      await load();
    } catch (err) {
      await dlg.error('Abo wurde nicht gelöscht', err);
    }
  };

  const subscribeUrl = (token: string): string => {
    return `${window.location.origin}/ical/${token}.ics`;
  };

  const copy = async (t: TokenRow): Promise<void> => {
    try {
      await navigator.clipboard.writeText(subscribeUrl(t.token));
      setCopiedId(t.id);
      setTimeout(() => setCopiedId((c) => (c === t.id ? null : c)), 1500);
    } catch {
      setError('Kopieren fehlgeschlagen — bitte URL manuell markieren');
    }
  };

  const toggleScope = (key: string): void => {
    setNewScopes((cur) => cur.includes(key) ? cur.filter((s) => s !== key) : [...cur, key]);
  };

  return (
    <div className="bg-slate-900 rounded-xl sm:rounded-lg p-5 sm:p-4 border border-slate-800 space-y-3" data-test-anchor="kalender-abos">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-brand-400" />
          <span className="text-slate-100 font-semibold text-sm">Kalender-Abos (iCal)</span>
        </div>
        {!showNew && (
          <button
            type="button"
            onClick={() => { setShowNew(true); setNewSaved(null); }}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-brand-600 hover:bg-brand-500 text-primary-foreground min-h-[44px]"
            data-test-anchor="kalender-abo-neu"
          >
            <Plus size={14} /> Neu
          </button>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Abonniere deinen Kalender in Outlook, Google Kalender, Apple Kalender oder Thunderbird.
        Pro Endgerät / App ein eigener Token — bei Verlust einfach löschen.
      </p>

      {error && (
        <div className="px-3 py-2 rounded-md bg-rose-500/15 text-rose-300 text-sm flex flex-wrap items-center gap-3" data-test-anchor="kalender-abos-fehler">
          <span>{error}</span>
          <button type="button" onClick={() => void load()} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-xs hover:bg-rose-500/10">
            <RefreshCw size={12} /> Erneut laden
          </button>
        </div>
      )}
      {newSaved && (
        <div className="px-3 py-2 rounded-md bg-emerald-500/15 text-emerald-300 text-sm" data-test-anchor="kalender-abo-angelegt">{newSaved}</div>
      )}

      {showNew && (
        <div className="rounded-md border border-slate-700 bg-slate-800/50 p-3 space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Beschreibung (optional)</label>
            <input
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="z.B. iPhone, Outlook-Buero"
              className="w-full h-10 px-3 rounded-md border border-slate-700 bg-slate-900 text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1">Enthalten:</div>
            <div className="flex flex-wrap gap-2">
              {ALL_SCOPES.map((s) => {
                const on = newScopes.includes(s.key);
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => toggleScope(s.key)}
                    className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                      on
                        ? 'bg-brand-500/15 text-brand-200 border-brand-500/50'
                        : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700'
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => void cancelNew()}
              disabled={creating}
              className="px-3 py-1.5 text-sm text-slate-300 hover:text-slate-100 min-h-[44px]"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void create()}
              disabled={creating || newScopes.length === 0}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-brand-600 hover:bg-brand-500 text-primary-foreground disabled:opacity-50 min-h-[44px]"
            >
              {creating && <Loader2 size={14} className="animate-spin" />}
              Abo anlegen
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-4 justify-center text-sm">
          <Loader2 size={14} className="animate-spin" /> Lädt …
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-center text-slate-500 py-4 text-xs">
          Noch keine Kalender-Abos angelegt.
        </div>
      ) : (
        <div className="space-y-2">
          {tokens.map((t) => (
            <div key={t.id} className="rounded-md border border-slate-800 bg-slate-800/30 p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-100 truncate">
                    {t.description || <span className="italic text-slate-400">Ohne Bezeichnung</span>}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {t.scopes.map((s) => {
                      const meta = ALL_SCOPES.find((x) => x.key === s);
                      return (
                        <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-slate-700 text-slate-300">
                          {meta?.label ?? s}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void remove(t)}
                  className="p-1.5 text-slate-400 hover:text-rose-400 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
                  title="Abo löschen"
                  aria-label="Abo löschen"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={subscribeUrl(t.token)}
                  readOnly
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  className="flex-1 min-w-0 h-8 px-2 rounded text-xs bg-slate-900 border border-slate-700 text-slate-300 font-mono"
                />
                <button
                  type="button"
                  onClick={() => void copy(t)}
                  className="inline-flex items-center gap-1 px-2 h-8 rounded text-xs bg-slate-700 hover:bg-slate-600 text-slate-200"
                  title="URL in Zwischenablage kopieren"
                >
                  {copiedId === t.id ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId === t.id ? 'Kopiert' : 'Kopieren'}
                </button>
                <button
                  type="button"
                  onClick={() => setOpenQr(t)}
                  className="inline-flex items-center gap-1 px-2 h-8 rounded text-xs bg-slate-700 hover:bg-slate-600 text-slate-200"
                  title="QR-Code fürs Handy anzeigen"
                >
                  <QrCode size={12} /> QR
                </button>
              </div>
              <div className="text-[10px] text-slate-500">
                Angelegt {formatDate(t.created_at)}
                {t.last_accessed_at && <> · Zuletzt abgerufen {formatDate(t.last_accessed_at)}</>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reine Anzeige: Escape und Klick daneben schließen ohne Rückfrage. */}
      <GuardedModal
        open={openQr != null}
        title={openQr?.description || 'Kalender-Abo'}
        dirty={false}
        onClose={() => setOpenQr(null)}
        size="sm"
        testAnchor="kalender-abo-qr"
      >
        {openQr && (
          <div className="space-y-3">
            <div className="rounded-lg bg-white p-3 flex items-center justify-center">
              <img
                src={`/api/kalender/subscribe-tokens/${openQr.id}/qr.svg`}
                alt="QR-Code fürs Kalender-Abo"
                className="w-full max-w-[240px] h-auto"
              />
            </div>
            <div className="text-xs text-slate-300 text-center">
              Mit dem Handy scannen → Kalender-App fragt „Abonnieren?“
            </div>
            <div className="text-[10px] text-slate-500 text-center font-mono break-all">
              {subscribeUrl(openQr.token)}
            </div>
          </div>
        )}
      </GuardedModal>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
